import { Play, Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Footer } from '@/features/landing/sections/Footer'
import { Nav } from '@/features/landing/sections/Nav'
import { ClosingCTA } from '@/features/marketing/components/ClosingCTA'
import { ExploreHero } from '@/features/marketing/components/ExploreHero'
import { FeatureBlock } from '@/features/marketing/components/FeatureBlock'
import { MockupFrame } from '@/features/marketing/components/MockupFrame'
import { StadiumDemoVisual } from '@/features/marketing/components/StadiumDemoVisual'
import { BracketMockup } from '@/features/marketing/mockups/BracketMockup'
import { CheckInMockup } from '@/features/marketing/mockups/CheckInMockup'
import { PrizePoolMockup } from '@/features/marketing/mockups/PrizePoolMockup'

/**
 * /explore/tournaments — tournaments deep-dive.
 *
 * Five feature blocks. Stadium is the largest and lives mid-page —
 * spectacle moment where the visitor is already convinced the
 * basics work and is open to "and there's THIS" energy.
 *
 *   1. Bracket formats        — "you can run a real bracket"
 *   2. Check-in + Start now   — "and start it when you're ready"
 *   3. Paid prize pools       — "and pay your winners"
 *   4. TV Stadium             — "and put it on the venue's TV"
 *   5. ELO / tournament stats — "and track who's actually good"
 */
export default function ExploreTournaments() {
  return (
    <div className="min-h-dvh bg-bg text-fg">
      <Nav />
      <main>
        <ExploreHero
          bucketLabel="Tournaments"
          headline={
            <>
              Host tournaments your<br />
              community will actually finish.
            </>
          }
          subline="From four-player free-for-alls to paid prize pools: seed it, schedule it, run it, pay out. Single elim, double elim, Swiss, round robin. All in the same flow your members already use for gaming sessions and stream nights."
          secondaryCtaLabel="See squadron management"
          secondaryCtaTo="/explore/squadrons"
          visual={
            <MockupFrame address="e404.app / event / bracket" className="max-w-lg">
              <div className="h-[280px]">
                <BracketMockup />
              </div>
            </MockupFrame>
          }
        />

        {/* Stadium-demo banner. Sits directly under the hero — the
            interactive demo is the single biggest USP on this page
            and gets missed when the only entry point lives 4 blocks
            down. Gold accent matches the GRAND FINAL palette in the
            Stadium itself so visitors recognise the surface from
            the live composition. */}
        <section className="border-b border-border/60 px-6 py-10">
          <div className="mx-auto max-w-6xl">
            <a
              href="/stadium/demo"
              className="group relative flex w-full flex-col items-stretch overflow-hidden rounded-2xl border-2 text-left transition-all hover:-translate-y-px md:flex-row md:items-center"
              style={{
                borderColor: 'rgba(255,179,0,0.4)',
                background:
                  'linear-gradient(120deg, rgba(255,179,0,0.10) 0%, rgba(217,70,239,0.06) 60%, rgba(61,111,255,0.10) 100%)',
                boxShadow:
                  '0 20px 60px -20px rgba(255,179,0,0.30), 0 0 0 1px rgba(255,179,0,0.18) inset',
              }}
            >
              {/* Left content */}
              <div className="flex flex-1 flex-col gap-3 p-6 md:p-8">
                <span
                  className="font-display inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-extrabold tracking-[0.28em] uppercase"
                  style={{
                    borderColor: 'rgba(255,179,0,0.4)',
                    color: '#FFB300',
                    backgroundColor: 'rgba(255,179,0,0.08)',
                  }}
                >
                  <Sparkles className="size-3" aria-hidden />
                  Live interactive demo
                </span>
                <h2 className="font-display text-balance text-2xl leading-tight font-bold md:text-3xl lg:text-4xl">
                  See the Tournament Stadium for yourself.
                </h2>
                <p className="max-w-xl text-pretty text-sm text-fg-muted md:text-base">
                  Pre-event countdown · live bracket · grand final ·
                  champion reveal. Walk through every phase of a real
                  tournament on your screen — no signup required.
                </p>
                <span
                  className="font-display mt-2 inline-flex w-fit items-center gap-2 rounded-lg px-5 py-3 text-sm font-extrabold tracking-wide text-bg transition-transform group-hover:translate-x-1"
                  style={{ backgroundColor: '#FFB300' }}
                >
                  <Play className="size-4 fill-current" aria-hidden />
                  See the demo
                </span>
              </div>

              {/* Right thumbnail — sells the visual upfront. Static
                  StadiumDemoVisual gives the watermarked still
                  without dragging the heavy phase player into the
                  hero band. */}
              <div className="relative hidden w-1/2 max-w-[520px] shrink-0 self-stretch overflow-hidden border-l md:flex" style={{ borderColor: 'rgba(255,179,0,0.25)' }}>
                <div className="absolute inset-0">
                  <StadiumDemoVisual />
                </div>
                {/* Soft fade so the thumbnail blends into the banner background */}
                <div
                  aria-hidden
                  className="absolute inset-y-0 left-0 w-24"
                  style={{
                    background:
                      'linear-gradient(90deg, rgba(10,8,18,0.6) 0%, transparent 100%)',
                  }}
                />
              </div>
            </a>
          </div>
        </section>

        <FeatureBlock
          eyebrow="01 · Bracket formats"
          headline={
            <>
              Single elim. Double elim.<br />
              Swiss. Round robin.
            </>
          }
          copy={
            <>
              Pick the format that matches your community. e404 generates
              the bracket, seeds teams (random, ELO-weighted, or manual
              drag-reorder), and runs the match graph end-to-end. Winners
              auto-advance. Losers in double-elim slide to the lower
              bracket. No spreadsheet, no bracket-builder URL surgery.
            </>
          }
          bullets={[
            'Bo1 / Bo3 / Bo5 / Bo7 per match — per-tournament default',
            'Auto-seeding from squad ELO, manual drag-reorder for tournaments where seeding is the point',
            '3rd-place bronze match toggle for single elim',
            'Match scheduling between captains, with dispute / override flow',
          ]}
          visual={
            <MockupFrame address="e404.app / event / bracket" className="max-w-xl">
              <div className="h-[340px]">
                <BracketMockup />
              </div>
            </MockupFrame>
          }
          side="image-right"
        />

        <FeatureBlock
          eyebrow="02 · Check-in + Start Now"
          headline="Confirm who's actually here. Then start."
          copy={
            <>
              Open a check-in window before the bracket locks in.
              Members tap "I'm here" from the app; no-shows get auto-
              dropped from the bracket. Got everyone in early? Hit
              <strong> Start Now</strong> and the bracket forms
              immediately — no waiting for the scheduled clock.
            </>
          }
          bullets={[
            'Configurable check-in window (default opens 30 min before start)',
            '"Start Now" host CTA — bypasses the schedule when the venue is ready',
            'Auto-drops members who don\'t check in (avoids the deadweight slot)',
            'Late joiners can still join up to lock-in time',
          ]}
          visual={
            <MockupFrame address="e404.app / event · check-in" className="max-w-lg">
              <CheckInMockup />
            </MockupFrame>
          }
        />

        <FeatureBlock
          eyebrow="03 · Paid prize pools"
          headline={
            <>
              Paid entry. Real prize pools.<br />
              Auto payouts to winners.
            </>
          }
          copy={
            <>
              Charge an entry fee. Winners get paid automatically when
              the bracket completes. Stripe Connect handles the
              payment processing; the squadron owner sponsors top-ups
              if they want to sweeten the pot. UK 18+ age verification
              is built in (Yoti integration), Gambling-Act-safe per
              the skill-predominant exception.
            </>
          }
          bullets={[
            'Default 50/30/20 split between top three, fully editable per tournament',
            'Owner top-up: sponsor £50 to seed the pool, no per-entrant cost',
            'Auto Stripe Connect payouts to winners — no manual transfers',
            '1% platform fee — the cheapest paid-tournament infrastructure in the space',
          ]}
          visual={
            <MockupFrame address="e404.app / event / pot" className="max-w-lg">
              <PrizePoolMockup />
            </MockupFrame>
          }
          side="image-right"
        />

        <FeatureBlock
          eyebrow="04 · Tournament Stadium"
          headline={
            <>
              Put a TV at your venue.<br />
              Make it feel like esports.
            </>
          }
          copy={
            <>
              Open the Stadium view on the venue TV. The whole
              tournament broadcasts itself: countdown to start,
              check-in counter, live bracket with winner-highlight
              animations, big VS card for the current match, then a
              champion reveal with a spark-burst when the final
              closes. One-tap setup from the host's phone, no
              software install.
            </>
          }
          bullets={[
            'Public 24-hour signed URL — no sign-in needed at the venue',
            'Scales 1080p TV → 4K → 8K LED video wall',
            'Phase-aware: pre-event countdown · check-in · live bracket · grand final · podium reveal',
            'Auto-updates every 5 seconds as results come in',
          ]}
          visual={
            <div className="flex w-full max-w-2xl flex-col gap-4">
              <MockupFrame address="e404.app / stadium / friday-bracket-night">
                <StadiumDemoVisual />
              </MockupFrame>
              {/* "See the demo" CTA — the inline twin to the gold
                  banner up at the top of the page. Sits directly
                  under the visual so the visitor associates the
                  prompt with the still they just looked at. Links
                  straight to the demo — no gate to route around. */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface/40 p-3 pl-4">
                <p className="text-sm text-fg-muted">
                  Want to feel it for real?{' '}
                  <span className="font-display font-bold text-fg">
                    Open the interactive demo.
                  </span>
                </p>
                <Button asChild variant="primary" size="sm">
                  <a href="/stadium/demo">
                    <Play className="size-3.5" aria-hidden />
                    See the demo
                  </a>
                </Button>
              </div>
            </div>
          }
        />

        <FeatureBlock
          eyebrow="05 · ELO + stats"
          headline="Every match counts. Every member ranked."
          copy={
            <>
              Tournament results feed into per-game ELO. Members carry
              their rating across every tournament you host. Captains
              get a head-to-head history view — handy when scheduling
              the next match against a rival. Top-ranked members
              surface as "rising" in your monthly squadron report.
            </>
          }
          bullets={[
            'Per-game ELO — Counter-Strike rating is independent from Apex rating',
            'Tournament W/L history per member, persisted across squadrons',
            'Head-to-head view: "you\'re 4-2 against Stormbreaker in CS2"',
            'Auto-feeds the manual-seed UI for serious tournaments',
          ]}
          visual={
            <MockupFrame address="e404.app / profile / tournament-stats" className="max-w-lg">
              <EloStatsPlaceholder />
            </MockupFrame>
          }
          side="image-right"
        />

        {/* Comparison strip */}
        <section className="border-b border-border/40 bg-surface/30 px-6 py-20">
          <div className="mx-auto max-w-5xl">
            <p className="font-display text-center text-xs font-bold tracking-[0.3em] text-fg-subtle uppercase">
              How e404 stacks up
            </p>
            <h2 className="font-display mt-4 text-center text-3xl leading-tight font-bold md:text-4xl">
              Bracket-only tools don't know about your squadron.
            </h2>
            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  name: 'Battlefy',
                  takeaway:
                    "Runs a clean bracket, but it doesn't know your squadron exists: members re-register from scratch every time, ratings reset between events, and there's nothing to put up on a venue screen.",
                },
                {
                  name: 'Smash.gg / start.gg',
                  takeaway:
                    "Handles a massive public bracket well; a Tuesday-night community cup is more setup than it needs, and paid entry means bolting on a third-party plugin.",
                },
                {
                  name: 'Guild Manager',
                  takeaway:
                    'Earns its keep on MMO raid rosters: DKP, attendance, applications. None of that comes with a bracket, a prize pool, a per-game ELO ladder, or a venue TV view.',
                },
                {
                  name: 'Discord polls',
                  takeaway:
                    'Work fine for four players picking a map. Past eight, nobody can tell who actually won, and nobody\'s getting paid regardless.',
                },
              ].map((c) => (
                <div
                  key={c.name}
                  className="rounded-xl border border-border bg-bg/40 p-5"
                >
                  <p className="font-display text-[10px] font-bold tracking-[0.25em] text-fg-subtle uppercase">
                    Versus
                  </p>
                  <h3 className="font-display mt-2 text-lg font-bold text-fg">
                    {c.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-fg-muted">
                    {c.takeaway}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClosingCTA
          location="tournaments"
          headline="Host your first tournament this weekend."
          subline="Free for unranked / unpaid tournaments. Gold tier unlocks paid prize pools and the TV stadium view."
          primaryCtaLabel="Host a tournament"
          primaryCtaTo="/signup"
        />
      </main>
      <Footer />
    </div>
  )
}

/**
 * Lightweight inline mockup for the ELO + tournament-stats block.
 * Full standalone mockup would be its own file; for v1 a compact
 * read-out here suffices.
 */
function EloStatsPlaceholder() {
  const rows = [
    { game: 'Counter-Strike 2', elo: 1742, change: '+24', w: 18, l: 7 },
    { game: 'Valorant', elo: 1620, change: '+12', w: 11, l: 6 },
    { game: 'Apex Legends', elo: 1488, change: '-8', w: 6, l: 9 },
    { game: 'Rocket League', elo: 1395, change: '+4', w: 4, l: 4 },
  ]
  return (
    <div className="divide-y divide-border/60 p-2">
      {rows.map((r) => {
        const positive = r.change.startsWith('+')
        return (
          <div key={r.game} className="flex items-center gap-3 px-3 py-3">
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-bold text-fg">
                {r.game}
              </p>
              <p className="mt-0.5 text-[11px] text-fg-subtle">
                {r.w}W · {r.l}L · {Math.round((r.w / (r.w + r.l)) * 100)}% win rate
              </p>
            </div>
            <div className="text-right">
              <p className="font-display text-lg font-black tabular-nums text-fg">
                {r.elo}
              </p>
              <p
                className={`font-display text-[11px] font-extrabold tabular-nums ${positive ? 'text-success' : 'text-danger'}`}
              >
                {r.change}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
